import type { ErrorLoggedEvent, Subscription } from '../../../../../types';

import * as styles from './index.css';
import { EventScopeProvider, useAnalytics, useEventScope } from '@blueshift-ui/analytics';
import ACCOUNT_ROUTES from '../../../../../constants/account-routes';
import BaseCard from '@blueshift-ui/surfaces/dist/components/base-card';
import { ConfirmationDialogTrigger } from '../../dialogs/confirmation';
import EVENT_NAMES from '../../../../../constants/generated/event-names';
import RichTranslation from '@blueshift-ui/i18n/dist/components/rich-translation';
import Snackbar from '@blueshift-ui/feedback/dist/components/snackbar';
import Typography from '@blueshift-ui/theme/dist/components/typography';
import { add } from 'date-fns';
import formatNumber from '../../../../../helpers/format-number';
import { useNavigate } from 'react-router-dom';
import useRichTranslation from '@blueshift-ui/i18n/dist/hooks/use-rich-translation';
import { useState } from 'react';
import useUpdateSubscription from '../../../../../hooks/subscription/use-update-subscription';

interface SubscriptionCardProps {
  currentSubscription: Subscription;
  newSubscription: Subscription;
}

function SubscriptionCard({ currentSubscription, newSubscription }: SubscriptionCardProps) {
  const analytics = useAnalytics();
  const eventScope = useEventScope();

  const { loading, translate } = useRichTranslation('membership.cancel.downgrade_card');
  const navigate = useNavigate();
  const { makeRequestUpdateSubscription } = useUpdateSubscription({
    currentSubscription,
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const hours = newSubscription.entitlements[0].configuration.number_of_hours;
  const price = formatNumber({
    currency: newSubscription.currency_code,
    number: newSubscription.purchase_price_cents / 100,
  });
  const supplementalRate = formatNumber({
    currency: newSubscription.currency_code,
    number: Number(newSubscription.supplemental_rate_cents_per_hour) / 100,
  });

  async function handleConfirm() {
    try {
      await makeRequestUpdateSubscription({
        parameters: {
          new_item_id: newSubscription.item_id,
          new_purchase_price_cents: newSubscription.purchase_price_cents,
          old_item_id: currentSubscription.item_id,
          old_purchase_price_cents: currentSubscription.purchase_price_cents,
        },
        scheduled_at: add(new Date(currentSubscription.next_billing_date), {
          hours: -2,
        }).toISOString(),
        transformation_type: 'membership_change_requested',
      });

      navigate(`/${ACCOUNT_ROUTES.membership}`);
    } catch (error: any) {
      analytics.track(EVENT_NAMES.errorLogged, {
        error_category: 'subscription_downgrade_failure',
        error_message: error instanceof Error ? error.message : String(error),
        error_severity: 'warning',
        page_path: window.location.pathname,
        page_section: eventScope?.pageSection,
        user_impact: 'frustrating',
      } satisfies ErrorLoggedEvent);

      setSnackbarOpen(true);

      throw error;
    }
  }

  if (loading) {
    return null;
  }

  return (
    <EventScopeProvider eventScope={{ pageSection: `try_${hours}_hour_plan_card` }}>
      <BaseCard
        actions={
          <ConfirmationDialogTrigger
            buttonProps={{
              children: (
                <RichTranslation
                  i18nKey="action_button_content"
                  replace={{ hours }}
                  translator={translate}
                />
              ),
              id: `try_${hours}_hour_plan_button`,
            }}
            dialogProps={{
              id: `try_${hours}_hour_plan_confirmation_dialog`,
              children: (
                <RichTranslation
                  i18nKey="confirmation_content"
                  replace={{ hours, price }}
                  translator={translate}
                />
              ),
              onAsyncConfirm: handleConfirm,
              title: (
                <RichTranslation
                  i18nKey="confirmation_title"
                  replace={{ hours }}
                  translator={translate}
                />
              ),
            }}
          />
        }
        className={styles.card}
      >
        <Typography className={styles.header} component="header">
          <RichTranslation i18nKey="header" replace={{ hours }} translator={translate} />
        </Typography>
        <div className={styles.content}>
          <RichTranslation
            i18nKey="content"
            replace={{ hours, price, supplementalRate }}
            translator={translate}
          />
        </div>
        <Snackbar
          message={<RichTranslation i18nKey="confirmation_error" translator={translate} />}
          onClose={() => setSnackbarOpen(false)}
          open={snackbarOpen}
          severity="error"
        />
      </BaseCard>
    </EventScopeProvider>
  );
}

export default SubscriptionCard;
